import { useCallback, useMemo, useState } from "react";
import { Check, Copy } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { api, ApiError } from "@/lib/api";
import { useAuthToken } from "@/lib/auth";
import {
  diagnosticsAnchorId,
  diagnosticsText,
  groupByVariant,
  stepNotes,
  variantLabel,
  type BenchDiagnosticsResponse,
  type BenchDiagnosticsRun,
} from "@/lib/benchDiagnostics";

function CopyText({ text, label }: { text: string; label: string }) {
  const [done, setDone] = useState(false);
  return (
    <button
      type="button"
      aria-label={label}
      title={done ? "Copied" : label}
      className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      onClick={() => {
        void navigator.clipboard
          .writeText(text)
          .then(() => {
            setDone(true);
            setTimeout(() => setDone(false), 1500);
          })
          .catch(() => undefined);
      }}
    >
      {done ? (
        <Check className="h-3 w-3" aria-hidden />
      ) : (
        <Copy className="h-3 w-3" aria-hidden />
      )}
    </button>
  );
}

/** A scrollable, wrapping monospace block with its copy button. */
function Block({
  title,
  text,
  tone,
}: {
  title: string;
  text: string;
  tone?: "warn";
}) {
  return (
    <div className="space-y-0.5" data-testid="diagnostic-block">
      <div className="flex items-center gap-1 text-xs font-medium">
        <span className="min-w-0 break-words">{title}</span>
        <CopyText text={text} label={`Copy ${title}`} />
      </div>
      <pre
        className={`max-h-64 overflow-auto rounded border bg-muted/40 p-2 font-mono text-[11px] leading-snug whitespace-pre-wrap break-words ${
          tone === "warn" ? "text-amber-700 dark:text-amber-400" : ""
        }`}
      >
        {text}
      </pre>
    </div>
  );
}

function RunBlock({ run }: { run: BenchDiagnosticsRun }) {
  const groups = groupByVariant(run.items);
  return (
    <div className="space-y-2 rounded border p-2" data-testid="bench-run">
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs">
        <span className="font-medium">bench run #{run.id}</span>
        <Badge variant="outline">{run.status}</Badge>
        <span className="text-muted-foreground">
          compiler {run.compilerVersion} · {run.createdAt}
        </span>
      </div>
      <div className="space-y-0.5 text-xs text-muted-foreground">
        <div>
          Ran on publish host{" "}
          <span className="font-mono">{run.publishHost ?? "unknown"}</span>
          {run.host && typeof run.host.hostId === "string" && (
            <>
              {" "}
              · bench host{" "}
              <span className="font-mono">{String(run.host.hostId)}</span>
              {typeof run.host.kernel === "string" && (
                <> · kernel {String(run.host.kernel)}</>
              )}
            </>
          )}
          {run.macosHosts.length > 0 && (
            <>
              {" "}
              · macOS host{run.macosHosts.length > 1 ? "s" : ""}{" "}
              <span className="font-mono">{run.macosHosts.join(", ")}</span>
            </>
          )}
          {run.runId && (
            <>
              {" "}
              · run <span className="font-mono">{run.runId}</span>
              {run.rev !== null && <> rev {run.rev}</>}
            </>
          )}
        </div>
        {run.variants.map((v) => (
          <div key={v.variant} className="break-words">
            <span className="font-medium">{variantLabel(v.variant)}:</span>{" "}
            {v.status}
            {v.reason ? ` — ${v.reason}` : ""}
          </div>
        ))}
      </div>
      {groups.length === 0 && (
        <div className="text-xs text-muted-foreground">
          No failure text was recorded for this run.
        </div>
      )}
      {groups.map((g) => (
        <div key={g.variant} className="space-y-1.5">
          <div className="text-xs font-semibold">{variantLabel(g.variant)}</div>
          {g.items.map((i, k) => (
            <Block
              key={`${i.where}-${i.kind}-${k}`}
              title={`${i.where} · ${i.kind}`}
              text={i.text}
            />
          ))}
        </div>
      ))}
    </div>
  );
}

/**
 * "Benchmark diagnostics" in the attempt drawer: the full DEGRADED notes of
 * the attempt's steps plus, per stored benchmark run and variant (glibc,
 * musl, macOS, fixtures), every raw failure message, exit code / stderr
 * lines, output-diff snippet and the host that ran it. Internal only: the
 * public registry never receives this text (private/auth benchRedact.ts).
 * The stored results are fetched when the section is first opened.
 */
/** Opens an attempt's diagnostics section (loading the stored results) and
 *  scrolls it into view. Used by the step panel's "View diagnostics" button. */
export function openBenchDiagnostics(attemptKey: string): void {
  const el = document.getElementById(diagnosticsAnchorId(attemptKey));
  if (!(el instanceof HTMLDetailsElement)) return;
  // the browser fires `toggle` itself, which loads the stored results
  el.open = true;
  el.scrollIntoView({ block: "center", behavior: "smooth" });
}

export function BenchDiagnostics({
  packageName,
  version,
  steps,
  attemptKey,
}: {
  packageName: string;
  version: string;
  steps: unknown;
  /** Identifies the attempt: the section's DOM id for openBenchDiagnostics. */
  attemptKey?: string;
}) {
  const token = useAuthToken();
  const notes = useMemo(() => stepNotes(steps), [steps]);
  const [runs, setRuns] = useState<BenchDiagnosticsRun[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(() => {
    if (loading) return;
    setLoading(true);
    setError(null);
    api<BenchDiagnosticsResponse>(
      token,
      `/publish/versions/${encodeURIComponent(packageName)}/${encodeURIComponent(version)}/diagnostics`,
    )
      .then((r) => setRuns(r.runs))
      .catch((err: unknown) =>
        setError(
          err instanceof ApiError
            ? err.message
            : "Could not load the benchmark diagnostics.",
        ),
      )
      .finally(() => setLoading(false));
  }, [token, packageName, version, loading]);

  const all = diagnosticsText(notes, runs ?? []);
  return (
    <details
      {...(attemptKey ? { id: diagnosticsAnchorId(attemptKey) } : {})}
      className="text-xs text-muted-foreground"
      data-testid="bench-diagnostics"
      onToggle={(e) => {
        if ((e.currentTarget as HTMLDetailsElement).open && runs === null)
          load();
      }}
    >
      <summary className="cursor-pointer select-none">
        Benchmark diagnostics
        {notes.length > 0
          ? ` (${notes.length} note${notes.length > 1 ? "s" : ""})`
          : ""}
      </summary>
      <div className="mt-1.5 space-y-2 text-foreground">
        <div className="flex items-center gap-1 text-xs text-muted-foreground">
          Internal only: raw errors, exit codes, stderr lines and diffs. Never
          shown on the public registry.
          {all && <CopyText text={all} label="Copy all diagnostics" />}
        </div>
        {notes.map((n) => (
          <Block key={n.title} title={n.title} text={n.text} tone="warn" />
        ))}
        {loading && (
          <div className="text-muted-foreground">Loading stored results…</div>
        )}
        {error && (
          <div className="text-red-600 dark:text-red-400" role="alert">
            {error}{" "}
            <button type="button" className="underline" onClick={load}>
              Retry
            </button>
          </div>
        )}
        {runs?.length === 0 && (
          <div className="text-muted-foreground">
            No stored benchmark result for this version yet.
          </div>
        )}
        {runs?.map((r) => (
          <RunBlock key={r.id} run={r} />
        ))}
      </div>
    </details>
  );
}
